import { SelectMode } from "./types";

/**
 * Parses an HTML string into a detached document.
 *
 * @param html - Raw HTML source.
 * @returns Parsed HTML document.
 */
export function parseHTML(html: string): Document {
  return new DOMParser().parseFromString(html, "text/html");
}

/**
 * Extracts selector-matched HTML from the provided markup.
 *
 * @param html - Raw HTML source.
 * @param selector - CSS selector to locate.
 * @param select - Extraction mode (`inner` or `outer`).
 * @returns Extracted HTML, or `null` when no valid match exists.
 */
export function extractHtmlBySelector(html: string, selector: string, select: SelectMode): string | null {
  const doc = parseHTML(html);

  try {
    const match = doc.querySelector(selector);
    if (!match) return null;
    return select === "inner" ? match.innerHTML : match.outerHTML;
  } catch {
    return null;
  }
}

/**
 * Extracts HTML payload from Shopify section-rendering responses.
 *
 * @param raw - Raw response body from the network.
 * @param sectionId - Preferred section identifier to extract.
 * @returns Extracted HTML payload.
 */
export function extractShopifyHtml(raw: string, sectionId: string): string {
  const maybeJson = parseJSON(raw);
  if (!maybeJson || typeof maybeJson !== "object") {
    const doc = parseHTML(raw);
    return doc.body ? doc.body.innerHTML : raw;
  }

  const sectionEntry = maybeJson as Record<string, unknown>;

  if (sectionId && typeof sectionEntry[sectionId] === "string") {
    return sectionEntry[sectionId] as string;
  }

  if (sectionEntry.sections && typeof sectionEntry.sections === "object") {
    const sections = sectionEntry.sections as Record<string, unknown>;
    if (sectionId && typeof sections[sectionId] === "string") {
      return sections[sectionId] as string;
    }

    return Object.values(sections)
      .filter((item): item is string => typeof item === "string")
      .join("");
  }

  return raw;
}

/**
 * Attempts to parse JSON from a response body.
 *
 * @param raw - Raw response body.
 * @returns Parsed JSON value or `null` when parsing does not apply.
 */
function parseJSON(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}
